<template>
	<transition
		v-if="campaignEnabled"
		enter-active-class="transition-all duration-500 ease-out"
		enter-from-class="translate-y-8 opacity-0 sm:translate-y-0 sm:translate-x-8"
		enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
		leave-active-class="transition-all duration-300 ease-in"
		leave-from-class="translate-y-0 opacity-100 sm:translate-x-0"
		leave-to-class="translate-y-8 opacity-0 sm:translate-y-0 sm:translate-x-8"
	>
		<div
			v-if="visible"
			class="fixed z-[100] print:hidden left-2 right-2 bottom-[5.5rem] sm:left-auto sm:right-4 sm:bottom-4 sm:w-[22rem] max-w-[26rem] mx-auto"
			role="dialog"
			aria-label="Free Worship Together starter kit"
		>
			<div class="relative rounded-2xl overflow-hidden shadow-2xl bg-[#2f3f41] text-white ring-1 ring-black/20">
				<div class="absolute inset-0 opacity-35 pointer-events-none">
					<!-- Site-wide: must not collide with any page banner illustration. -->
					<img src="/images/wt-brand-closing.jpg" class="w-full h-full object-cover object-center" alt="" />
				</div>
				<div class="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none"
					style="background-image:
						radial-gradient(circle at 18% 25%, rgba(78,111,112,0.55), transparent 55%),
						radial-gradient(circle at 82% 70%, rgba(220,174,99,0.35), transparent 55%);" />
				<div class="absolute inset-0 bg-gradient-to-b from-[#2f3f41]/70 via-[#2f3f41]/85 to-[#2f3f41] pointer-events-none" />

				<button
					@click="dismiss"
					class="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white/80 hover:text-white transition-colors"
					aria-label="Dismiss"
				>
					<i class="fas fa-times text-xs" />
				</button>

				<a
					:href="kitUrl"
					@click="trackClick"
					class="relative block px-5 py-5 group"
				>
					<div class="flex items-start gap-3">
						<img
							src="/images/wt-brand-logo.png"
							alt=""
							class="w-12 h-12 shrink-0 object-contain drop-shadow-lg"
						/>
						<div class="flex-1 min-w-0">
							<div class="text-[9px] font-semibold uppercase tracking-[0.22em] text-[#dcae63]">
								Free Starter Kit
							</div>
							<div class="font-serif font-bold text-lg leading-tight mt-1">
								Start Sunday.
							</div>
							<div class="font-serif italic text-sm text-white/75 mt-1 leading-snug">
								A free kit to help your family worship together at church.
							</div>
						</div>
					</div>
					<div class="mt-4 flex justify-end">
						<span
							class="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#cc9d4f] text-white text-xs font-semibold tracking-wide shadow-md transition-all group-hover:bg-[#b98b44] group-hover:shadow-lg"
						>
							Get the free kit
							<i class="fas fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" />
						</span>
					</div>
				</a>
			</div>
		</div>
	</transition>
</template>

<script>
import createCampaignElementMixin from '../../helpers/campaignElementMixin'
import { shouldSuppressPopup, wasDismissed, markDismissed, hasSignedUp, anyPromoPopupShown, markPromoPopupShown } from '../../helpers/campaignSuppress'

const KIT_PATH = '/worship-together'
const UTM = 'utm_source=cfl-site&utm_medium=slidein-popup&utm_campaign=worship-together&utm_content=25s-timer'
const DELAY_MS = 25_000
const DISMISS_KEY = 'fwKitSlideinDismissed'
const SHOWN_KEY = 'fwKitSlideinShown'
const EXCLUDED_PATTERNS = [/^\/(es|cn)(\/|$|\?)/i, /^\/worship-together(\/|$|\?)/i, /^\/restore-family-worship(\/|$|\?)/i, /checkout/i, /confirmation/i, /starter-kit/i, /thank/i]

export default {
	mixins: [createCampaignElementMixin('slidein-popup')],
	data() {
		return {
			visible: false,
			armed: false,
		}
	},
	computed: {
		kitUrl() {
			return `${KIT_PATH}?${UTM}`
		},
		onExcludedRoute() {
			const path = this.$route?.path || ''
			return EXCLUDED_PATTERNS.some(re => re.test(path))
		},
	},
	mounted() {
		// Admin artwork preview: show immediately, skip the timer/suppression gate.
		if (this.preview) {
			this.visible = true
			return
		}
		// Never arm the timer if the visitor dismissed this before (persisted) or
		// has already signed up.
		if (shouldSuppressPopup(DISMISS_KEY)) return
		try {
			if (window.sessionStorage.getItem(SHOWN_KEY) === '1') return
		} catch {}
		this._timer = window.setTimeout(() => {
			this.armed = true
			this.maybeShow()
		}, DELAY_MS)
	},
	beforeUnmount() {
		if (this._timer) window.clearTimeout(this._timer)
	},
	watch: {
		$route() {
			if (!this.armed) return
			if (this.onExcludedRoute) {
				this.visible = false
			} else {
				this.maybeShow()
			}
		},
	},
	methods: {
		maybeShow() {
			// A pop-up that is switched OFF must not consume the one-promo-per-session slot.
			// Without this, a disabled pop-up still fired its timer, called markPromoPopupShown()
			// and silently suppressed a pop-up the admin had switched ON (HAP-3654). Checked here
			// rather than in mounted() because the flags load asynchronously — at mount time this
			// still holds the offline fallback value.
			if (!this.campaignEnabled) return
			if (this.visible) return
			if (this.onExcludedRoute) return
			// Re-check at show time — the visitor may have signed up or dismissed
			// (e.g. the exit-intent popup) during the 25s the timer was running.
			if (shouldSuppressPopup(DISMISS_KEY)) return
			// Only one promo pop-up per session — if the book-offer modal (or the
			// exit-intent popup) already fired, stand down.
			if (anyPromoPopupShown()) return
			try {
				if (window.sessionStorage.getItem(SHOWN_KEY) === '1') return
				window.sessionStorage.setItem(SHOWN_KEY, '1')
			} catch {}
			this.visible = true
			markPromoPopupShown()
			this.trackImpression()
		},
		dismiss() {
			this.visible = false
			markDismissed(DISMISS_KEY)
			this.trackCampaignDismiss()
		},
		trackImpression() {
			this.trackCampaignImpression()
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_worship_kit_slidein_impression', {
						event_category: 'engagement',
						event_label: 'slidein-popup',
						route: this.$route?.path,
					})
				}
			} catch {}
		},
		trackClick() {
			this.trackCampaignClick({ link_url: this.kitUrl })
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_worship_kit_slidein_click', {
						event_category: 'engagement',
						event_label: 'slidein-popup',
						link_url: this.kitUrl,
						route: this.$route?.path,
					})
				}
			} catch {}
			try {
				window.sessionStorage.setItem(DISMISS_KEY, '1')
				window.sessionStorage.setItem('fwKitConverted', '1')
			} catch {}
		},
	},
}
</script>
